<template>
    <div id="answer">
        <div class="mb-5" v-if="!$global.loading && errorMessage === null">
            <div class="container w-75 ml-auto mr-auto mt-4" style="padding: 0">
                <div class="justify-content-between d-flex">
                    <div>
                        <div style="font-size: 19px" class="page-title d-flex">
                            <div class="d-flex">
                                <div class="mt-auto mb-auto">
                                    {{ provider.name }}
                                </div>
                                <div class="mt-auto mb-auto ml-2 mr-2" v-if="provider.apidae_source !== null">—</div>
                                <div class="mt-auto mb-auto" v-if="provider.apidae_source !== null">
                                    {{ provider.apidae_source.external_id }}
                                </div>
                                <div class="mt-auto mb-auto" v-else-if="provider.tourinsoft_source !== null">
                                    {{ provider.tourinsoft_source.external_id }}
                                </div>
                            </div>

                            <div v-if="provider.apidae_source !== null">
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" icon target="_blank" :href="provider.apidae_source.url">
                                            <v-icon>open_in_new</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Voir sur Apidae</span>
                                </v-tooltip>
                            </div>
                        </div>

                        <div>
                            <span>
                                Réponse de <b>{{ answer.first_name }} {{ answer.last_name }}</b> (<a
                              :href="'mailto:' + answer.email + '?subject=Au sujet de ' + provider.name">{{
                                    answer.email }}</a>)<br />
                                Reçue le {{ formatDate(answer.updated_at) }}
                            </span>

                            <span>
                                —
                            </span>

                            <span>
                                <a class="mt-2" :href="apiUrl + '/export-to-pdf/cmaj/' + answer.id">
                                    Télécharger le PDF
                                </a>
                            </span>
                        </div>
                    </div>

                    <div class="mt-auto" v-if="answer.web_form.state.code !== 'TREATED'">
                        <div v-if="lastWrite !== null" class="last-write">Dernière mise à jour le {{ lastWrite }}</div>

                        <div class="d-flex">
                            <div v-if="selectedMode === 1">
                                <div>
                                    <v-btn class="default-button label-button" v-if="provider.apidae_source !== null"
                                           :disabled="!answer.open_this_season || is404Source" @click="writeTis">
                                        Faire une demande de brouillon sur Apidae
                                    </v-btn>
                                    <v-btn v-else-if="provider.tourinsoft_source !== null" class="default-button"
                                           @click="writeTis">
                                        Envoyer sur Tourinsoft
                                    </v-btn>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="mt-auto d-flex" v-else>
                        <div>
                            <div v-if="lastWrite !== null" class="last-write">Dernière mise à jour le {{ lastWrite }}
                            </div>
                            <v-btn v-if="provider.apidae_source !== null" class="default-button"
                                   :disabled="!answer.open_this_season || is404Source"
                                   @click="writeTis">
                                Faire une demande de brouillon sur Apidae
                            </v-btn>

                            <v-btn v-else-if="provider.tourinsoft_source !== null" class="default-button"
                                   @click="writeTis">
                                Envoyer sur Tourinsoft
                            </v-btn>
                        </div>
                    </div>

                </div>

                <div class="mt-5">
                    <v-alert type="warning" text class="container" v-if="is404Source">
                        La fiche n'existe pas sur Apidae

                        <div class="w-100 text-center mt-4">
                            <v-btn color="warning" @click="writeTis">
                                Créer la fiche sur Apidae
                            </v-btn>
                        </div>
                    </v-alert>
                </div>

                <div class="mt-5" v-if="!answer.open_this_season">
                    <v-alert type="warning" text class="container">
                        Le prestataire déclare avoir cessé cette activité.
                    </v-alert>
                </div>

                <div class="mt-5" v-if="hasManyAnswers">
                    <v-alert type="warning" text class="container" v-if="selectLast">
                        Cette réponse n'est pas la réponse originale du prestataire.

                        <v-btn class="default-button ml-3" @click="switchVersion">Voir la réponse originale</v-btn>
                    </v-alert>

                    <v-alert type="success" text class="container" v-else>
                        Cette réponse a été modifiée le <b>{{ lastModification }}</b>.

                        <v-btn class="default-button ml-3" @click="switchVersion">Voir la réponse modifiée</v-btn>
                    </v-alert>
                </div>
            </div>

            <div class="mt-5 mb-3 mr-auto ml-auto form-container container" style="padding: 0"
                 v-if="answer.extra !== null && answer.extra !== ''">
                <h5>Informations complémentaires</h5>

                <v-textarea class="textarea extra-area" style="background-color: white" readonly v-model="answer.extra"
                            outlined>
                </v-textarea>
            </div>

            <div class="mt-5 mb-3 form-container mr-auto ml-auto answer-content container"
                 v-if="answer.open_this_season">
                <div v-if="answer.open_this_season">
                    <general :languages="languages" :read-only="false" class="mt-5"></general>

                    <localisation :read-only="false"></localisation>

                    <contacts :contacts="contacts" :read-only="false"></contacts>

                    <medias :read-only="false" class="mt-5"></medias>

                    <room-renting class="mt-5"></room-renting>

                    <specific :read-only="false" class="mt-5"></specific>

                    <opening-hours :read-only="false"></opening-hours>

                    <price-periods :read-only="false" class="mt-5"></price-periods>

                    <capacities class="mt-5"></capacities>

                    <covid></covid>
                </div>
            </div>
        </div>

        <div v-if="!$global.loading && errorMessage !== null" class="mt-5 mb-5 app-container">
            <v-alert type="error" text border="right" colored-border>
                {{ errorMessage }}
            </v-alert>
        </div>

    </div>
</template>

<script>
import Covid from "./tabulations/Covid"
import { mapGetters, mapMutations, mapState } from "vuex"
import General from "./tabulations/General"
import Localisation from "./tabulations/Localisation"
import Contacts from "./tabulations/Contacts"
import Medias from "./tabulations/Medias"
import Specific from "./tabulations/Specific"
import Capacities from "./tabulations/Capacities"
import RoomRenting from "./tabulations/RoomRenting"
import OpeningHours from "./tabulations/OpeningHours"
import PricePeriods from "./tabulations/PricePeriods"
import hash from "object-hash"
import _ from "lodash"

export default {
    name: "UnlogedAnswer",
    components: {
        PricePeriods,
        OpeningHours,
        RoomRenting, Capacities, Specific, Medias, Contacts, Localisation, General, Covid,
    },
    data() {
        return {
            token: null,
            answer: null,
            provider: null,
            baseProviderHash: null,
            contacts: [],
            languages: [],
            apiUrl: process.env.VUE_APP_API_URL,
            isDirty: false,
            lastWrite: null,
            lastModification: null,
            selectLast: false,
            campaign: null,
            hasManyAnswers: false,
            errorMessage: null,
            selectedMode: 1,
            is404Source: false,
            referent: null,
        }
    },
    methods: {
        ...mapMutations("web_form", [
            "resetState", "setProvider", "setFields", "setDisabled", "setFamilyElements", "setExtra",
            "setCapacities", "setRoomRenting", "setCurrencies", "setCategoryFamilies",
        ]),
        getAnswer() {
            this.$global.loading = true

            this.baseProviderHash = null
            this.isDirty = null

            this.$http.get("webform/answer/" + this.token + "/" + this.selectLast).then(res => {
                if (res.body.success) {
                    let provider = res.body.data.provider

                    this.answer = res.body.data.answer
                    this.campaign = res.body.data.campaign
                    this.hasManyAnswers = res.body.data.hasManyAnswers
                    this.is404Source = res.body.data.is404Source
                    this.referent = res.body.data.referent

                    let lastWriteAction = res.body.data.lastWriteAction
                    let lastModificationAction = res.body.data.lastModificationAction

                    if (lastWriteAction !== null) {
                        this.lastWrite = this.$moment(lastWriteAction.pivot.created_at).format("DD-MM-YYYY à HH[h]mm")
                    }

                    if (lastModificationAction !== null) {
                        this.lastModification = this.$moment(lastModificationAction.pivot.created_at).format("DD-MM-YYYY à HH[h]mm")
                    }

                    this.formatProviderData(provider)

                    this.setProvider(provider)
                    this.setFields(res.body.data.fields)
                    this.setFamilyElements(res.body.data.referential)
                    this.setExtra(this.answer.extra)
                    this.setRoomRenting(res.body.data.provider.room_renting)
                    this.setCategoryFamilies(res.body.data.categoryFamilies)

                    let capacities = this.getFamilyByCode("Capacites")

                    this.setCapacities(capacities)

                    this.provider = provider

                    document.title = "Dahub – " + this.provider.name

                    this.setIntercom()

                    this.$global.loading = false
                } else if (res.body.code === 404) {
                    this.errorMessage = "La fiche n'existe plus sur Dahub. Elle a du être supprimée depuis le tableau" +
                      " des prestataires. La réponse associée à cette fiche n'est donc plus disponible."

                    this.$global.loading = false
                    this.loaded = true
                }
            })
        },
        formatProviderData(provider) {
            this.formatPricePeriods(provider)
        },
        formatPricePeriods(provider) {
            provider.price_periods.forEach(period => {
                period.prices.forEach(price => {
                    let mode = "MIN_MAX"

                    if (price.min === price.max) {
                        mode = "UNIQUE"
                    } else if (price.max === null) {
                        mode = "FROM"
                    }

                    price.min = price.min / 100
                    price.max = price.max / 100

                    price.mode = mode
                })
            })
        },
        getCommunicationTypes() {
            this.$http.get("resource/communication-types").then(res => {
                this.contacts = res.body.data.contacts
            })
        },
        writeTis() {
            this.$forceUpdate()

            this.$global.loading = true
            this.selectLast = true

            setTimeout(() => {
                if (this.isDirty) {
                    this.saveAndWrite()
                } else {
                    this.write()
                }
            }, 500)
        },
        saveAndWrite() {
            let provider = JSON.parse(JSON.stringify(this.provider))

            provider = this.fixData(provider)

            let payload = {
                answerId: this.answer.id,
                provider: this.provider,
                skipSso: true,
            }

            let tis = this.provider.apidae_source !== null ? "apidae" : "tourinsoft"

            this.$http.post(tis + "/save-write", payload).then(res => {
                this.getAnswer()
            })
        },
        write() {
            let payload = {
                answerId: this.answer.id,
                providerDataId: this.provider.id,
                skipSso: true,
            }

            let tis = this.provider.apidae_source !== null ? "apidae" : "tourinsoft"

            this.$http.post(tis + "/write", payload).then(res => {
                this.$global.loading = false
            })
        },
        loginAgain() {
            window.location.href = this.apiUrl + "/sso/attempt/" + btoa(window.location.href)
        },
        getLanguages() {
            this.$http.get("resource/languages").then(res => {
                this.languages = res.body.data.languages
            })
        },
        getCurrencies() {
            this.$http.get("resource/currencies").then(res => {
                this.setCurrencies(res.body.data.currencies)
            })
        },
        fixData(provider) {
            if (provider.localisation.commune !== undefined && provider.localisation.commune !== null) {
                provider.localisation.commune_id = provider.localisation.commune.id
            }

            provider.capacities = this.capacities.filter(capacity => {
                return capacity.value !== undefined && capacity.value !== ""
            })

            provider.room_renting = this.roomRenting

            provider.price_periods.forEach(period => {
                period.prices.map(price => {
                    if (price.mode === "UNIQUE") {
                        price.max = price.min
                    } else if (price.mode === "FROM") {
                        price.max = null
                    }
                })
            })

            if (provider.room_renting !== undefined) {
                provider.room_renting.rooms.forEach(room => {
                    if (room.area !== null) {
                        room.area = room.area.toString().replace(",", ".")
                    }
                })
            }

            return provider
        },
        getB64(id) {
            return btoa(id)
        },
        switchVersion() {
            this.selectLast = !this.selectLast

            this.getAnswer()
        },
        setIntercom() {
            if (this.user !== null) {
                let userConf = {
                    user_id: this.user.id,
                    name: this.user.first_name + " " + this.user.last_name,
                    email: this.user.email,
                    role: this.user.role.name,
                    account_configured: this.user.account.configured,
                }

                Object.assign(window.intercomSettings, userConf)

                window.Intercom("update", window.intercomSettings)
            } else if (this.referent !== null) {
                let userConf = {
                    name: this.referent.organism_name,
                    email: this.referent.email,
                }

                Object.assign(window.intercomSettings, userConf)

                window.Intercom("update", window.intercomSettings)
            }

            window.Intercom("boot", window.intercomSettings)
        },
    },
    computed: {
        ...mapState("web_form", ["capacities"]),
        ...mapState("account", ["account", "user"]),
        ...mapGetters("web_form", ["hasField", "hasFamilyElement", "getFamilyByCode", "getElementsByFamilyCode"]),
    },
    watch: {
        provider: {
            handler: _.debounce(function() {
                if (this.baseProviderHash === null) {
                    this.baseProviderHash = hash(this.provider)
                }

                this.isDirty = this.baseProviderHash !== hash(this.provider)
            }, 500),
            deep: true,
        },
    },
    created() {
        this.token = this.$route.params.token

        this.resetState()
        this.setDisabled(true)

        this.getAnswer()
        this.getCommunicationTypes()
        this.getLanguages()
        this.getCurrencies()
    },
}
</script>
